import React from 'react';
import { SubmissionStep } from '../types';

interface ProgressStepProps {
  steps: SubmissionStep[];
  isComplete: boolean;
  hasError: boolean;
}

const ProgressStep: React.FC<ProgressStepProps> = ({
  steps,
  isComplete,
  hasError,
}) => {
  const completedSteps = steps.filter(s => s.status === 'completed').length;
  const progress = Math.round((completedSteps / steps.length) * 100);

  return (
    <div className="space-y-6">
      <div className="text-center">
        {hasError ? (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Setup Failed</h3>
            <p className="mt-1 text-sm text-gray-500">
              An error occurred during the setup process.
            </p>
          </>
        ) : (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
              <svg
                className="h-8 w-8 animate-spin text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Creating...</h3>
            <p className="mt-1 text-sm text-gray-500">
              Please wait while we set up your merchant.
            </p>
          </>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mx-auto max-w-md">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700">Progress</span>
          <span className="text-gray-500">{progress}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
          <div
            className={`h-full transition-all duration-500 ${
              hasError ? 'bg-red-500' : 'bg-primary-600'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Steps List */}
      <div className="mx-auto max-w-md space-y-2">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`flex items-center gap-3 rounded-lg p-3 ${
              step.status === 'completed'
                ? 'bg-green-50'
                : step.status === 'failed'
                ? 'bg-red-50'
                : step.status === 'in_progress'
                ? 'bg-primary-50'
                : 'bg-gray-50'
            }`}
          >
            {/* Status Icon */}
            <div className="flex-shrink-0">
              {step.status === 'completed' ? (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                  <svg
                    className="h-4 w-4 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              ) : step.status === 'failed' ? (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
                  <svg
                    className="h-4 w-4 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              ) : step.status === 'in_progress' ? (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100">
                  <svg
                    className="h-4 w-4 animate-spin text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                </div>
              ) : (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200">
                  <span className="text-xs font-medium text-gray-500">{index + 1}</span>
                </div>
              )}
            </div>

            {/* Label */}
            <div className="flex-1">
              <span
                className={`text-sm font-medium ${
                  step.status === 'completed'
                    ? 'text-green-900'
                    : step.status === 'failed'
                    ? 'text-red-900'
                    : step.status === 'in_progress'
                    ? 'text-primary-900'
                    : 'text-gray-500'
                }`}
              >
                {step.label}
              </span>
              {step.error && (
                <p className="mt-0.5 text-xs text-red-600">{step.error}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressStep;
